import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';

import {useGate, useStore} from 'effector-react';
import {ControllerRenderProps, FieldError, useForm} from 'react-hook-form';
import {Controller} from 'react-hook-form/dist/index.ie11';
import {useTranslation} from 'react-i18next';

import {toast} from 'react-toastify';

import * as yup from 'yup';

import {ReactComponent as HelperIcon} from 'assets/svg/helper.svg';

import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Helper} from 'components/ui-kit/Helper';
import {HtmlText} from 'components/ui-kit/HTMLText';
import {Icon} from 'components/ui-kit/Icon';
import {Input} from 'components/ui-kit/Input';
import {Switch} from 'components/ui-kit/Switcher';
import {FIRSGate, firsState$, setCredentialsFIRSFx} from 'models/profile/common';

import {colors} from 'theme';
import {CredentialsFIRS} from 'types';

import {ButtonsContainer, Field, FormFields, Heading} from '../styled';

import {DescriptionContainer, RowContainer} from './styled';

type FormField = {
    name: keyof CredentialsFIRS;
    readonly?: boolean;
};

export const CredentialsFIRSForm = () => {
    useGate(FIRSGate);
    const {t, i18n} = useTranslation();
    const translateParticipantForm = useCallback(
        (key: string) => t('pages.profile.firs.fields.' + key),
        [i18n.language],
    );

    const [collapsed, setCollapsed] = useState(false);
    const [edit, setEdit] = useState(false);

    const firs = useStore(firsState$);

    const schema: yup.SchemaOf<CredentialsFIRS> = yup.object().shape({
        businessId: yup.string().required(t('pages.profile.firs.validations.required')),
        apiKey: yup.string().required(t('pages.profile.firs.validations.required')),
        apiSecret: yup.string().required(t('pages.profile.firs.validations.required')),
        serviceId: yup.string().required(t('pages.profile.firs.validations.required')),
        publicKey: yup.string().required(t('pages.profile.firs.validations.required')),
        certificate: yup.string().required(t('pages.profile.firs.validations.required')),
    });

    const {
        control,
        formState: {errors},
        handleSubmit,
        reset,
    } = useForm<CredentialsFIRS>({
        resolver: yupResolver(schema),
        mode: 'onChange',
    });

    const onSubmit = async (payload: CredentialsFIRS) => {
        console.log(payload);
        try {
            await setCredentialsFIRSFx(payload);
            setEdit(false);
            toast.success(t('pages.profile.firs.messages.update'));
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        if (firs.data) reset(firs.data);
        else reset();
    }, [firs.data]);

    const formFields: FormField[] = useMemo(
        () => [
            {name: 'businessId'},
            {name: 'apiKey'},
            {name: 'apiSecret'},
            {name: 'serviceId'},
            {name: 'publicKey'},
            {name: 'certificate'},
        ],
        [t, collapsed],
    );

    const controlRender = (
        controlEvents: ControllerRenderProps<CredentialsFIRS>,
        field: FormField,
    ) => {
        const baseProps = {
            placeholder: translateParticipantForm(field.name),
            hasError: edit && !!errors?.[field.name],
            defaultValue: firs.data?.[field.name] ?? '',
            readOnly: !edit,
        };

        const props = {...baseProps, ...controlEvents};

        return <Input {...props} />;
    };

    return (
        <>
            <Heading style={{marginTop: 20}}>
                <div style={{display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer'}}>
                    <span style={{flex: '1 0 auto'}}>{t('pages.profile.firs.title')}</span>
                    <Switch
                        defaultVal={collapsed}
                        value={collapsed}
                        onChange={() => setCollapsed(!collapsed)}
                    />
                </div>
            </Heading>
            {collapsed && (
                <>
                    <form>
                        <FormFields customGrid="repeat(4, 1fr)">
                            {formFields.slice(0, 4).map((field) => (
                                <Field
                                    key={field.name}
                                    text={(errors?.[field.name] as FieldError)?.message}
                                    isError={!!errors?.[field.name]}
                                >
                                    <span>{translateParticipantForm(field.name)}</span>
                                    <Controller
                                        name={field.name}
                                        control={control}
                                        defaultValue=""
                                        render={(props) => controlRender(props, field)}
                                    />
                                </Field>
                            ))}
                        </FormFields>
                        <DescriptionContainer>
                            <RowContainer>
                                <HtmlText
                                    Tag="h3"
                                    __html={t('pages.profile.firs.description.add')}
                                />
                                <Helper
                                    maxWidth="410px"
                                    content={
                                        <HtmlText
                                            Tag="span"
                                            __html={t('pages.profile.firs.description.helperText')}
                                        />
                                    }
                                >
                                    <RowContainer>
                                        <h4>{t('pages.profile.firs.description.where')}</h4>
                                        <Icon
                                            source={HelperIcon}
                                            width={18}
                                            height={18}
                                            fillColor="transparent"
                                            style={{color: colors.brandPrimary}}
                                        />
                                    </RowContainer>
                                </Helper>
                            </RowContainer>
                            <HtmlText __html={t('pages.profile.firs.description.enter')} />
                        </DescriptionContainer>
                        <FormFields customGrid="repeat(2, 1fr)">
                            {formFields.slice(-2).map((field) => (
                                <Field
                                    key={field.name}
                                    text={(errors?.[field.name] as FieldError)?.message}
                                    isError={!!errors?.[field.name]}
                                >
                                    <span>{translateParticipantForm(field.name)}</span>
                                    <Controller
                                        name={field.name}
                                        control={control}
                                        defaultValue=""
                                        render={(props) => controlRender(props, field)}
                                    />
                                </Field>
                            ))}
                        </FormFields>
                    </form>
                    <ButtonsContainer>
                        {edit ? (
                            <Button
                                onClick={() => {
                                    setEdit(false);
                                    reset();
                                }}
                                variant={ButtonVariant.Outline}
                                isLoading={firs.loading}
                            >
                                {t('pages.profile.firs.buttons.cancel')}
                            </Button>
                        ) : (
                            <Button
                                onClick={() => setEdit(true)}
                                variant={ButtonVariant.Secondary}
                                isLoading={firs.loading}
                            >
                                {t('pages.profile.firs.buttons.edit')}
                            </Button>
                        )}
                        <Button
                            onClick={handleSubmit(onSubmit)}
                            disabled={!edit}
                            isLoading={firs.loading}
                        >
                            {t('pages.profile.firs.buttons.save')}
                        </Button>
                    </ButtonsContainer>
                </>
            )}
        </>
    );
};
